'use client';

import { API_ROUTES, useApiQuery } from '@shared/api-utils';
import { Button, Skeleton } from '@shared/components/shadcn';
import { getSiteConfig } from '@shared/config';
import { ChevronDown, ChevronUp } from '@shared/icons';
import { useTranslations } from 'next-intl';
import { useCallback, useEffect, useMemo, useState } from 'react';
import type { PaymentInstrumentsResponse, PaymentMethod } from '../my-account/Wallet/types';
import { PaymentInstrumentIcon } from '../PaymentInstrumentIcon/PaymentInstrumentIcon';
import { filterAllowedPayoutInstruments } from './instrumentFilters';
import { DEPOSIT_PAYMENT_METHODS, WITHDRAW_PAYMENT_METHODS } from './PaymentMethods';
import { PaymentMethodSelectorItem } from './PaymentMethodSelectorItem';
import type { MethodOrInstrument, PaymentMethodSelectorProps, PayoutInstrument, PayoutInstrumentsResponse } from './types';

function PaymentMethodsContainer({ children }: { children: React.ReactNode }) {
  return <div className="grid grid-cols-3 gap-3">{children}</div>;
}

function PaymentMethodSelector({ value, onChange, disabled, type = 'deposit', namespace }: PaymentMethodSelectorProps) {
  const tGeneral = useTranslations('general');
  const [showAllPaymentMethods, setShowAllPaymentMethods] = useState(false);
  const { allowedInstrumentTypes } = getSiteConfig().WITHDRAWAL;

  const { data: payoutInstruments, isLoading: isLoadingPayoutInstruments } = useApiQuery<PayoutInstrumentsResponse>(API_ROUTES.PAYMENTS.PAYOUTS.GET_CASHIER_INFO, {
    credentials: true,
    queryOptions: {
      enabled: type === 'withdraw',
    },
  });

  const { data: payinInstruments, isLoading: isLoadingPayinInstruments } = useApiQuery<PaymentInstrumentsResponse>(API_ROUTES.MY_ACCOUNT.PAYMENT_INSTRUMENTS, {
    credentials: true,
    queryOptions: {
      enabled: type === 'deposit',
    },
  });

  const getMappedInstruments = useCallback(() => {
    const instruments =
      type === 'withdraw' ? filterAllowedPayoutInstruments(payoutInstruments?.instruments ?? [], allowedInstrumentTypes) : payinInstruments?.paymentInstruments || [];
    return instruments.map((instrument: PayoutInstrument | PaymentMethod) => {
      const name = 'paymentMethodName' in instrument ? instrument.paymentMethodName : instrument.name;
      const apiName = 'paymentMethodType' in instrument ? instrument.paymentMethodType || 'WORLDPAYCARD' : 'WORLDPAYCARD';
      const apiReference = 'apiReference' in instrument ? instrument.apiReference : instrument.id || '';
      const instrumentType = 'type' in instrument ? instrument.type : undefined;
      const provider = instrumentType ? String(instrumentType).toLowerCase() : apiName?.toLowerCase();
      return {
        icon: <PaymentInstrumentIcon type={instrumentType} />,
        name,
        'data-track-method': name,
        'data-track-provider': apiReference ? provider : undefined,
        apiName,
        number: instrument.number,
        apiReference,
        expiryMonth: 'expiryMonth' in instrument ? instrument.expiryMonth : '',
        expiryYear: 'expiryYear' in instrument ? instrument.expiryYear : '',
        minAllowedAmount: 'minDepositAmount' in instrument ? instrument.minDepositAmount : undefined,
        maxAllowedAmount: 'maxDepositAmount' in instrument ? instrument.maxDepositAmount : undefined,
      };
    });
  }, [payoutInstruments, payinInstruments, type, allowedInstrumentTypes]);

  const isAppleDevice = useMemo(() => {
    return /iPad|iPhone|iPod|Mac|Apple Watch/.test(navigator.userAgent);
  }, []);

  const filteredPaymentMethods: MethodOrInstrument[] = useMemo(() => {
    const methods = Object.values(type === 'deposit' ? DEPOSIT_PAYMENT_METHODS : WITHDRAW_PAYMENT_METHODS).filter(method => {
      if (!isAppleDevice) {
        return method.name !== DEPOSIT_PAYMENT_METHODS.APPLE_PAY.name;
      }
      return true;
    });

    const applePayMethod = isAppleDevice && type === 'deposit' ? methods.find(method => method.name === DEPOSIT_PAYMENT_METHODS.APPLE_PAY.name) : undefined;
    const otherMethods = applePayMethod ? methods.filter(method => method.name !== DEPOSIT_PAYMENT_METHODS.APPLE_PAY.name) : methods;
    const instruments = getMappedInstruments();

    if (applePayMethod) {
      return [applePayMethod, ...instruments, ...otherMethods];
    }

    return [...instruments, ...methods];
  }, [isAppleDevice, type, getMappedInstruments]);

  const [methodsToShow, setMethodsToShow] = useState<MethodOrInstrument[]>(filteredPaymentMethods.slice(0, 3));

  useEffect(() => {
    if (showAllPaymentMethods) {
      // eslint-disable-next-line react-hooks/set-state-in-effect
      setMethodsToShow(filteredPaymentMethods);
    } else {
      setMethodsToShow(filteredPaymentMethods.slice(0, 3));
    }
  }, [showAllPaymentMethods, filteredPaymentMethods]);

  const [internalValue, setInternalValue] = useState<string>('');
  const [selectedMethod, setSelectedMethod] = useState<string>(value || internalValue);

  // Preselect the first instrument or method
  useEffect(() => {
    const firstMethod = filteredPaymentMethods[0]?.apiReference ?? filteredPaymentMethods[0]?.id ?? filteredPaymentMethods[0]?.name ?? '';
    // eslint-disable-next-line react-hooks/set-state-in-effect
    setInternalValue(firstMethod);
    setSelectedMethod(value || firstMethod);
  }, [setInternalValue, setSelectedMethod, filteredPaymentMethods, value]);

  const handleMethodChange = useCallback(
    (methodOrInstrument: MethodOrInstrument) => {
      const identifier = methodOrInstrument.apiReference ?? methodOrInstrument.name;
      if (onChange) {
        onChange(methodOrInstrument);
      } else {
        setInternalValue(identifier);
      }
      setSelectedMethod(identifier);
    },
    [onChange, setInternalValue]
  );

  // Preselect the first method if no value is provided
  useEffect(() => {
    if (onChange && !isLoadingPayoutInstruments && !isLoadingPayinInstruments && filteredPaymentMethods.length > 0 && (!value || value === '')) {
      const firstMethod = filteredPaymentMethods[0];
      if (firstMethod) {
        onChange(firstMethod);
      }
    }
  }, [onChange, filteredPaymentMethods, isLoadingPayoutInstruments, isLoadingPayinInstruments, value]);

  // Sync onChange when value prop is provided and matches a method/instrument
  useEffect(() => {
    if (onChange && !isLoadingPayoutInstruments && !isLoadingPayinInstruments && filteredPaymentMethods.length > 0 && value && value !== '') {
      const matchingMethodOrInstrument = filteredPaymentMethods.find(method => method.apiReference === value || method.name === value);
      if (matchingMethodOrInstrument) {
        onChange(matchingMethodOrInstrument);
      }
    }
  }, [onChange, filteredPaymentMethods, isLoadingPayoutInstruments, isLoadingPayinInstruments, value]);

  return (
    <div data-track-container="Methods">
      {isLoadingPayoutInstruments || isLoadingPayinInstruments ? (
        <PaymentMethodsContainer>
          {Array.from({ length: methodsToShow.length }).map((_, index) => (
            <Skeleton key={index} className="w-full h-19" />
          ))}
        </PaymentMethodsContainer>
      ) : (
        <>
          <PaymentMethodsContainer>
            {methodsToShow.map((method, index) => (
              <div key={method.apiReference || method.name} className="h-full min-w-0">
                <PaymentMethodSelectorItem
                  icon={method.icon}
                  name={method.number ?? method.label ?? method.name}
                  isSelected={selectedMethod === (method.apiReference ?? method.name)}
                  disabled={disabled}
                  className="w-full h-full"
                  onClick={() => handleMethodChange(method)}
                  data-track-name={method.apiReference ? 'Saved Method' : 'Select Method'}
                  data-track-method={method['data-track-method']}
                  data-track-provider={method['data-track-provider']}
                  data-track-position={index + 1}
                  type={method.apiReference ? 'instrument' : 'method'}
                  data-testid={`payment-method-selector-${method.name}`}
                  isApplePay={method.apiName === 'APPLEPAY'}
                  namespace={namespace}
                  expiryMonth={method.expiryMonth}
                  expiryYear={method.expiryYear}
                />
              </div>
            ))}
          </PaymentMethodsContainer>
          {filteredPaymentMethods.length > 3 && (
            <Button
              className="mt-3 justify-self-center flex gap-2"
              variant="ghost"
              onClick={() => setShowAllPaymentMethods(!showAllPaymentMethods)}
              data-testid="show-more-button"
              data-track-name={showAllPaymentMethods ? 'Show Less' : 'Show More'}
            >
              {showAllPaymentMethods ? <ChevronUp className="shrink-0" size="extra-small" /> : <ChevronDown className="shrink-0" size="extra-small" />}{' '}
              {showAllPaymentMethods ? tGeneral('show_less') : tGeneral('show_more')}
            </Button>
          )}
        </>
      )}
    </div>
  );
}

export { PaymentMethodSelector };
